import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { ArrowDown, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=2000&q=85";

const lines = ["Innus", "Lace Centre", "Art & Craft"];

export const Hero = () => {
  const ref = useRef(null);
  const [heroImg, setHeroImg] = useState(HERO_IMG);

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((r) => {
        if (r.data?.hero_image_url) setHeroImg(r.data.hero_image_url);
      })
      .catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0 scale-110">
        <img
          src={heroImg}
          alt="Inside a fabric and craft boutique — shelves of colour"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D1B36]/90 via-[#2D1B36]/55 to-[#2D1B36]/20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#2D1B36]/40 via-transparent to-[#FBF9F6]" />
      </motion.div>

      <motion.div
        style={{ y: textY, opacity: fade }}
        className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-24 md:pb-28"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="font-accent text-[11px] md:text-xs tracking-[0.4em] uppercase text-[#DFCA9B] mb-6 flex items-center gap-3"
        >
          <MapPin size={13} strokeWidth={1.5} />
          Karuna Complex · Court Road · N. Paravur
        </motion.p>

        <h1 className="font-serif-d font-light text-[#FBF9F6] leading-[0.92] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[9.5rem]">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#DFCA9B]" : ""} ${
                  i === 2 ? "text-[0.42em] tracking-[0.08em] mt-3" : ""
                }`}
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{
                  duration: 1.1,
                  delay: 0.25 + i * 0.16,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#shop"
            data-testid="hero-shop-button"
            className="bg-[#C5A059] text-[#2D1B36] font-accent text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-[#DFCA9B] transition-colors duration-300"
          >
            Shop the Collection
          </a>
          <a
            href="#story"
            data-testid="hero-story-button"
            className="border border-[#FBF9F6]/50 text-[#FBF9F6] font-accent text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-[#FBF9F6]/10 transition-colors duration-300"
          >
            Our Story
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-3 text-[#FBF9F6]/70"
      >
        <span className="font-accent text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center translate-y-[-12px]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown size={15} strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </section>
  );
};
